import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import {
  BACKGROUND_BLUR_STRENGTH_DEFAULT,
  BACKGROUND_BLUR_STRENGTH_MAX,
  BACKGROUND_BLUR_STRENGTH_MIN,
  BACKGROUND_BLUR_STRENGTH_STEP,
  isBackgroundBlurEnabled,
  parseBackgroundBlurStrength,
  PreferenceKey,
  usePreferencesStore,
} from '@/entities/preference'

import { isBackgroundBlurSupported } from '../lib/is-background-blur-supported'
import { updateBackgroundBlurStrengthOnActive } from '../lib/maybe-apply-background-blur'
import { reapplyBackgroundBlurOnActiveSessions } from '../lib/reapply-background-blur-on-sessions'

export const useBackgroundBlurPreference = () => {
  const preferencesStore = usePreferencesStore()
  const { preferences } = storeToRefs(preferencesStore)
  const { setPreferences } = preferencesStore

  const isSupported = computed(() => isBackgroundBlurSupported())

  const isEnabled = computed({
    get: () => isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED]),
    set: (value: boolean) => {
      void (async () => {
        await setPreferences(PreferenceKey.BACKGROUND_BLUR_ENABLED, value)
        // Explicit reapply: do not rely only on the global preference watch
        // (can race with deferred camera-on upgrade in conferences).
        await reapplyBackgroundBlurOnActiveSessions()
      })()
    },
  })

  const strength = computed({
    get: () => parseBackgroundBlurStrength(
      preferences.value[PreferenceKey.BACKGROUND_BLUR_STRENGTH] ?? BACKGROUND_BLUR_STRENGTH_DEFAULT,
    ),
    set: (value: number) => {
      const next = parseBackgroundBlurStrength(value)
      void (async () => {
        await setPreferences(PreferenceKey.BACKGROUND_BLUR_STRENGTH, next)
        if (isBackgroundBlurEnabled(preferences.value[PreferenceKey.BACKGROUND_BLUR_ENABLED])) {
          updateBackgroundBlurStrengthOnActive(next)
        }
      })()
    },
  })

  return {
    isSupported,
    isEnabled,
    strength,
    strengthMin: BACKGROUND_BLUR_STRENGTH_MIN,
    strengthMax: BACKGROUND_BLUR_STRENGTH_MAX,
    strengthStep: BACKGROUND_BLUR_STRENGTH_STEP,
  }
}
